import { Fragment, type ReactNode } from 'react';

import { ENTITY_CONFIG } from '@apps/web/components/phoebe_chat/entity_visuals';
import {
    ReferenceChip,
    ReferenceChipTray,
} from '@apps/web/components/phoebe_chat/reference_chip';
import { ShiftReferenceSection } from '@apps/web/components/phoebe_chat/shift_reference_section';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import {
    buildChatReferenceContextKey,
    chatReferenceStore,
    type ChatReferenceType,
    type StagedReference,
    useChatReferences,
} from '@apps/web/lib/chat_reference_store';
import { useMode } from '@apps/web/lib/mode_store';
import { cn } from '@apps/web/lib/utils';

type ReferenceSectionProps = {
    readonly type: ChatReferenceType;
    readonly references: ReadonlyArray<StagedReference>;
};

function GenericReferenceSection({ type, references }: ReferenceSectionProps) {
    const { icon: Icon, color } = ENTITY_CONFIG[type];
    return (
        <ReferenceChipTray testId={`chat-reference-tray-${type}`}>
            {references
                .filter((reference) => reference.type !== 'shift')
                .map(({ id, displayName }) => (
                    <ReferenceChip
                        key={id}
                        leading={
                            <Icon className={cn('size-4 shrink-0', color)} />
                        }
                        removeLabel={`Remove ${displayName} from message`}
                        removeTestId="chat-reference-remove"
                        rowTestId="chat-reference-row"
                        onRemove={() => chatReferenceStore.remove(id)}
                    >
                        <span className="min-w-0 flex-1 truncate text-xs leading-4 font-semibold">
                            {displayName}
                        </span>
                    </ReferenceChip>
                ))}
        </ReferenceChipTray>
    );
}

// Types without a bespoke section render as generic icon + name rows.
const SECTION_RENDERERS: Partial<
    Record<ChatReferenceType, (props: ReferenceSectionProps) => ReactNode>
> = {
    shift: ShiftReferenceSection,
};

export function ReferenceTray() {
    const { contextKey, orderedReferences } = useChatReferences();
    const organizationId = useOrganizationId();
    const mode = useMode() ?? 'live';
    if (orderedReferences.length === 0) {
        return null;
    }
    // A selection made under another org or mode is stale here; hide it
    // without clearing so it reappears when the user switches back.
    if (
        contextKey !== null &&
        contextKey !== buildChatReferenceContextKey(organizationId, mode)
    ) {
        return null;
    }

    const sections = new Map<ChatReferenceType, Array<StagedReference>>();
    for (const reference of orderedReferences) {
        const section = sections.get(reference.type);
        if (section) {
            section.push(reference);
        } else {
            sections.set(reference.type, [reference]);
        }
    }

    return (
        <Fragment>
            {[...sections].map(([type, references]) => {
                const Section =
                    SECTION_RENDERERS[type] ?? GenericReferenceSection;
                return (
                    <Section key={type} type={type} references={references} />
                );
            })}
        </Fragment>
    );
}
